import { api } from "@/lib/api";
import type { Role } from "@/lib/roles";

/**
 * Shared contract for the admin-managed Resource library. Mirrors
 * app/models/resource_content.py and app/schemas/resource_content.py — keep the
 * three in sync when adding a type, status, or content type.
 */

export type ResourceType = "glossary" | "faq" | "news" | "mastermind";
export type ResourceStatus = "draft" | "published" | "archived";
export type ResourceSourceType = "upload" | "external";

export interface ResourceContent {
  id: string;
  resource_type: ResourceType;
  /** Term (glossary) / Question (faq) / Title (news, mastermind) */
  title: string;
  /** Definition (glossary) / Answer (faq) / Content (news) / Description (mastermind) */
  body: string | null;
  short_description: string | null;
  /** The category's NAME (resolved server-side), not an id. */
  category: string | null;
  /** Set for types with a managed category list — see hasManagedCategories. */
  category_id: string | null;
  /** URL-safe form of the category name; what the reader-facing filter keys off. */
  category_slug: string | null;
  content_type: string | null;
  source_type: ResourceSourceType | null;
  /** Admin routes only — the role-facing feed returns null and sets has_file. */
  file_url: string | null;
  file_name: string | null;
  file_mime_type: string | null;
  file_size_bytes: number | null;
  has_file: boolean;
  external_url: string | null;
  thumbnail_url: string | null;
  display_order: number;
  status: ResourceStatus;
  published_at: string | null;
  is_active: boolean;
  /** roles.id values this resource is published to. Admin routes only. */
  role_ids: number[];
  created_at: string;
  updated_at: string;
}

/**
 * A role the admin may publish to. Served by GET /resources/roles straight from
 * the roles table, filtered by PUBLISHABLE_ROLE_NAMES on the server — the
 * frontend never hardcodes role names or ids, so enabling Seller server-side
 * makes it appear here with no frontend change.
 */
export interface ResourceRoleOption {
  id: number;
  name: string;
  label: string;
}

export interface PaginatedResources {
  items: ResourceContent[];
  total: number;
  page: number;
  page_size: number;
}

export interface ResourceUploadResult {
  file_url: string;
  file_name: string;
  file_mime_type: string;
  file_size_bytes: number;
}

/**
 * Roles allowed to reach the shared Resource pages (Glossary / FAQ / News).
 *
 * Mirrors PUBLISHABLE_ROLE_NAMES in app/models/resource_content.py, plus admin,
 * who previews everything. Seller is absent on purpose: Seller resource
 * functionality has not shipped, so sellers are redirected away rather than
 * shown pages with nothing in them.
 *
 * org_admin is here because the API already grants it read access to every
 * published resource — see the org-admin branch of _caller_role_ids in
 * app/routers/resources.py. Leaving it out made RoleRoute bounce a tenant's
 * admin to "/" on a library the server was willing to serve them. Authoring
 * stays shut to them (require_resource_author), and Mastermind stays shut too:
 * that route names its roles literally, matching _STAFF_ROLES server-side.
 *
 * TO ENABLE SELLER LATER: add "seller" here and to PUBLISHABLE_ROLE_NAMES
 * server-side. Those two lists are the only places the role set is declared —
 * the route guards, the nav entry points and the API filter all read from them.
 *
 * Mastermind is NOT covered by this list; it is staff-only and has its own
 * guard (see STAFF_ONLY_RESOURCE_TYPES / the /resources/mastermind route).
 */
export const RESOURCE_PAGE_ROLES: Role[] = ["buyer", "bl_advisor", "admin", "org_admin"];

export const RESOURCE_TYPES: ResourceType[] = ["glossary", "faq", "news", "mastermind"];

export const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  glossary: "Glossary",
  faq: "FAQ",
  news: "Latest News & Articles",
  mastermind: "Mastermind",
};

export const RESOURCE_STATUSES: ResourceStatus[] = ["draft", "published", "archived"];

export const RESOURCE_STATUS_LABELS: Record<ResourceStatus, string> = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};

export const MASTERMIND_CONTENT_TYPES = [
  "zoom_recording",
  "video",
  "cheat_sheet",
  "pdf",
  "document",
  "presentation",
  "template",
  "external_link",
  "other",
] as const;

/**
 * Content types NOT offered when Resource Type = Mastermind. The type stays in
 * MASTERMIND_CONTENT_TYPES (and in the backend's CONTENT_TYPES) so existing
 * records keep working and other resource types can still use it — it is only
 * filtered out of the Mastermind picker.
 */
export const MASTERMIND_HIDDEN_CONTENT_TYPES: string[] = ["video"];

/** Content types selectable when Resource Type = Mastermind. */
export const MASTERMIND_SELECTABLE_CONTENT_TYPES = MASTERMIND_CONTENT_TYPES.filter(
  (c) => !MASTERMIND_HIDDEN_CONTENT_TYPES.includes(c),
);

/**
 * Content types that can only ever point at an external URL — never an upload.
 * The source picker is hidden for these, the source is forced to "external",
 * and the URL is required. Mirrored by EXTERNAL_ONLY_CONTENT_TYPES in
 * app/models/resource_content.py, which enforces the same rule server-side.
 */
export const EXTERNAL_ONLY_CONTENT_TYPES: string[] = ["zoom_recording", "external_link"];

export const isExternalOnlyContentType = (contentType: string | null | undefined): boolean =>
  !!contentType && EXTERNAL_ONLY_CONTENT_TYPES.includes(contentType);

export const MASTERMIND_CONTENT_TYPE_LABELS: Record<string, string> = {
  zoom_recording: "Zoom Recording",
  video: "Video",
  cheat_sheet: "Cheat Sheet",
  pdf: "PDF",
  document: "Document",
  presentation: "Presentation",
  template: "Template",
  external_link: "External Link",
  other: "Other",
};

// ── Mastermind thumbnails ────────────────────────────────────────────
// Mastermind resources never carry a manually uploaded thumbnail: the image is
// derived from the content type, here, in one place. Deriving rather than
// persisting means the thumbnail can never go stale when the content type is
// edited, and no historical data has to be migrated.
//
// Files are real assets in frontend/public/images/mastermind/ and are served
// from the site root, the same way AppSidebar references /logo_mark.png.
const MASTERMIND_THUMBNAIL_DIR = "/images/mastermind";

export const MASTERMIND_THUMBNAILS: Record<string, string> = {
  zoom_recording: `${MASTERMIND_THUMBNAIL_DIR}/zoom-recording.svg`,
  cheat_sheet: `${MASTERMIND_THUMBNAIL_DIR}/cheat-sheet.svg`,
  pdf: `${MASTERMIND_THUMBNAIL_DIR}/pdf.svg`,
  document: `${MASTERMIND_THUMBNAIL_DIR}/document.svg`,
  presentation: `${MASTERMIND_THUMBNAIL_DIR}/presentation.svg`,
  template: `${MASTERMIND_THUMBNAIL_DIR}/template.svg`,
  external_link: `${MASTERMIND_THUMBNAIL_DIR}/external-link.svg`,
  other: `${MASTERMIND_THUMBNAIL_DIR}/other.svg`,
};

/** Used for any content type without its own image, and as the null fallback. */
export const MASTERMIND_FALLBACK_THUMBNAIL = MASTERMIND_THUMBNAILS.other;

/**
 * Shown on a news card when the article has no uploaded thumbnail, or when the
 * uploaded one fails to load. Keeps every card's image area the same size
 * instead of some cards having no image region at all.
 */
export const NEWS_FALLBACK_THUMBNAIL = "/images/news/placeholder.svg";

export const mastermindThumbnail = (contentType: string | null | undefined): string =>
  MASTERMIND_THUMBNAILS[contentType ?? ""] ?? MASTERMIND_FALLBACK_THUMBNAIL;

/**
 * The image to show for any resource. Mastermind derives from content type;
 * every other type keeps its uploaded thumbnail. Single entry point so no
 * component needs its own per-content-type conditionals.
 */
export function resourceThumbnail(
  resource: Pick<ResourceContent, "resource_type" | "content_type" | "thumbnail_url">,
): string | null {
  if (resource.resource_type === "mastermind") return mastermindThumbnail(resource.content_type);
  return resource.thumbnail_url;
}

/** Per-type field labels, so one form can title its shared inputs correctly. */
export const TITLE_LABELS: Record<ResourceType, string> = {
  glossary: "Term",
  faq: "Question",
  news: "Title",
  mastermind: "Title",
};

export const BODY_LABELS: Record<ResourceType, string> = {
  glossary: "Definition",
  faq: "Answer",
  news: "Content",
  mastermind: "Description",
};

/** Types whose body field is mandatory — matches _BODY_REQUIRED_TYPES server-side. */
export const BODY_REQUIRED_TYPES: ResourceType[] = ["glossary", "faq", "news"];

/** Published + active content of one type, in the admin-defined order.
 *  The server filters by the authenticated user's own roles — no role is sent.
 *
 *  Paging is opt-in: omit `limit` to get the whole library (Glossary, FAQ,
 *  Mastermind filter client-side). News passes `limit` because it grows without
 *  bound — see fetchResourcePage. */
export const fetchPublishedResources = (
  resourceType: ResourceType,
  { search, limit, offset }: { search?: string; limit?: number; offset?: number } = {},
) => {
  const params = new URLSearchParams({ resource_type: resourceType });
  if (search?.trim()) params.set("search", search.trim());
  if (limit !== undefined) params.set("limit", String(limit));
  if (offset) params.set("offset", String(offset));
  return api.get<ResourceContent[]>(`/resources?${params}`);
};

/**
 * One page of a feed, plus whether another page exists. Asks for one row more
 * than it returns, so the API can keep answering with a plain list.
 */
export async function fetchResourcePage(
  resourceType: ResourceType,
  { search, pageSize, offset }: { search?: string; pageSize: number; offset: number },
): Promise<{ items: ResourceContent[]; hasMore: boolean }> {
  const rows = await fetchPublishedResources(resourceType, { search, limit: pageSize + 1, offset });
  return { items: rows.slice(0, pageSize), hasMore: rows.length > pageSize };
}

/** Roles currently available as publishing targets (admin only).
 *  Takes the caller's client for the same reason the category helpers do — the
 *  admin screen is reused by the super admin console, and `api` would send the
 *  portal token to a super-admin-authenticated door (401). */
export const fetchPublishableRoles = (client: ResourceHttpClient = api) =>
  client.get<ResourceRoleOption[]>("/resources/roles");

/** One published resource. The server re-checks role access by id. */
export const fetchResource = (id: string) => api.get<ResourceContent>(`/resources/${id}`);

/** Route of the internal article detail page. */
export const newsArticlePath = (id: string) => `/resources/news/${id}`;

/**
 * Where a resource can be seen the way readers see it, or null when there is
 * no such page for it yet. News has a per-article page that admins can open
 * at any status. The other libraries are single list pages that only show
 * published rows, so a draft has nowhere to land.
 */
export function resourceReaderPath(
  resource: Pick<ResourceContent, "id" | "resource_type" | "status">,
): string | null {
  if (resource.resource_type === "news") return newsArticlePath(resource.id);
  return resource.status === "published" ? `/resources/${resource.resource_type}` : null;
}

/**
 * Where "Read More" should go for a news article: the configured external link
 * when there is one, otherwise the internal detail page. Single source of truth
 * so the card and any future surface can't disagree.
 */
export function newsReadMoreTarget(
  article: Pick<ResourceContent, "id" | "external_url">,
): { kind: "external"; href: string } | { kind: "internal"; to: string } {
  const external = article.external_url?.trim();
  return external
    ? { kind: "external", href: external }
    : { kind: "internal", to: newsArticlePath(article.id) };
}

/** "Buyer, BL Advisor" — for the grid column and the publish confirmation. */
export function formatRoleLabels(
  roleIds: number[] | undefined,
  roles: ResourceRoleOption[],
): string {
  if (!roleIds?.length) return "—";
  const labels = roleIds
    .map((id) => roles.find((r) => r.id === id)?.label)
    .filter((l): l is string => !!l);
  // A mapping to a role that isn't currently publishable (e.g. legacy data)
  // is surfaced rather than silently dropped.
  const unknown = roleIds.length - labels.length;
  if (unknown > 0) labels.push(`${unknown} other${unknown === 1 ? "" : "s"}`);
  return labels.join(", ");
}

/**
 * Open an uploaded asset. The blob URL is never sent to the browser — bytes are
 * streamed through an authenticated endpoint that re-checks the caller's role,
 * so the fetch needs the bearer token and can't be a plain <a href>.
 */
export async function openResourceFile(
  resource: Pick<ResourceContent, "id" | "file_name">,
  { download = false }: { download?: boolean } = {},
): Promise<void> {
  const blob = await api.blob(`/resources/${resource.id}/file${download ? "?download=true" : ""}`);
  const url = URL.createObjectURL(blob);
  if (download) {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = resource.file_name ?? "resource";
    anchor.click();
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
  // Revoke on the next tick — revoking synchronously can cancel the in-flight
  // navigation the click/open just started.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const formatPublishedDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";

export function formatFileSize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Managed categories ───────────────────────────────────────────────
/**
 * An admin-managed category a resource entry can be filed under. Mirrors
 * ResourceCategory in app/models/resource_content.py.
 *
 * Replaces the free-text category box for the types listed in
 * RESOURCE_TYPES_WITH_CATEGORIES; the rest still type a plain string.
 */
export interface ResourceCategory {
  id: string;
  resource_type: ResourceType;
  name: string;
  /** Derived from the name server-side — never sent on a write. */
  slug: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
  /** How many entries are filed here. Populated on the admin list only. */
  entry_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Types that pick their category from a managed list instead of typing free
 * text. Mirrors RESOURCE_TYPES_WITH_CATEGORIES in
 * app/models/resource_content.py — enabling another type there is what makes it
 * appear here, so the two lists must be changed together.
 */
export const RESOURCE_TYPES_WITH_CATEGORIES: ResourceType[] = ["glossary", "faq", "news"];

export const hasManagedCategories = (resourceType: ResourceType): boolean =>
  RESOURCE_TYPES_WITH_CATEGORIES.includes(resourceType);

export interface ResourceCategoryPayload {
  name: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
}

/**
 * The subset of the api client these helpers need.
 *
 * Taken as an argument rather than closed over, because the admin screen is
 * reused by two consoles and must issue its calls through the client it was
 * handed (see AdminResourceContent's `client` prop) — hardcoding `api` here
 * would send a tenant console's writes to the wrong door. Reader-facing callers
 * just take the default.
 */
export interface ResourceHttpClient {
  get: <T>(url: string) => Promise<T>;
  post: <T>(url: string, data: unknown) => Promise<T>;
  put: <T>(url: string, data: unknown) => Promise<T>;
  patch: <T>(url: string, data: unknown) => Promise<T>;
  delete: <T>(url: string) => Promise<T>;
}

/** Active categories only — the reader-facing filter and the form's picker. */
export const fetchResourceCategories = (
  resourceType: ResourceType = "glossary",
  client: ResourceHttpClient = api,
) => client.get<ResourceCategory[]>(`/resources/categories?resource_type=${resourceType}`);

/** Admin manager: deactivated categories included, each with its entry count. */
export const fetchResourceCategoriesForAdmin = (
  resourceType: ResourceType = "glossary",
  client: ResourceHttpClient = api,
) => client.get<ResourceCategory[]>(`/resources/categories/admin?resource_type=${resourceType}`);

export const createResourceCategory = (
  payload: ResourceCategoryPayload,
  resourceType: ResourceType = "glossary",
  client: ResourceHttpClient = api,
) =>
  client.post<ResourceCategory>("/resources/categories", {
    ...payload,
    resource_type: resourceType,
  });

export const updateResourceCategory = (
  id: string,
  payload: ResourceCategoryPayload,
  client: ResourceHttpClient = api,
) => client.put<ResourceCategory>(`/resources/categories/${id}`, payload);

/** Refused by the server while entries still reference the category. */
export const deleteResourceCategory = (id: string, client: ResourceHttpClient = api) =>
  client.delete<void>(`/resources/categories/${id}`);

export const reorderResourceCategories = (
  categoryIds: string[],
  resourceType: ResourceType = "glossary",
  client: ResourceHttpClient = api,
) =>
  client.patch<ResourceCategory[]>(
    `/resources/categories/reorder?resource_type=${resourceType}`,
    { category_ids: categoryIds },
  );

/**
 * Group published entries by category, in the admin's category order, with any
 * uncategorised entries last.
 *
 * Shared by the Glossary and FAQ pages so the grouping rule lives in one place
 * rather than being re-derived per surface.
 */
export function groupByCategory<T extends Pick<ResourceContent, "category" | "category_id">>(
  items: T[],
  categories: ResourceCategory[],
): { category: ResourceCategory | null; items: T[] }[] {
  const buckets = new Map<string, T[]>();
  const UNCATEGORISED = "";

  for (const item of items) {
    const key = item.category_id ?? UNCATEGORISED;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }

  // Annotated rather than inferred: the uncategorised bucket pushed below has a
  // null category, which a bare inference from this map would reject.
  const groups: { category: ResourceCategory | null; items: T[] }[] = categories
    .filter((c) => buckets.has(c.id))
    .map((c) => ({ category: c, items: buckets.get(c.id) ?? [] }));

  // Entries whose category was deactivated (so it is absent from `categories`)
  // would otherwise vanish from the grouped view entirely — collect them with
  // the genuinely uncategorised ones rather than dropping them.
  const placed = new Set(categories.map((c) => c.id));
  const leftovers = [...buckets.entries()]
    .filter(([key]) => key === UNCATEGORISED || !placed.has(key))
    .flatMap(([, group]) => group);

  if (leftovers.length) groups.push({ category: null, items: leftovers });
  return groups;
}
